import React, { useEffect, useState, useCallback } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    CircularProgress,
    Modal,
    TextField,
    IconButton,
    Menu,
    MenuItem,
    Alert,
} from '@mui/material';
import { MoreVert, MapOutlined } from '@mui/icons-material';
import TopNav from './TopNav.jsx';
import Footer from './Footer.jsx';
import GlassCard from './GlassCard.jsx';
import ActionButton from './ActionButton.jsx';
import useDocumentTitle from '../utils/useDocumentTitle';
import { PALETTE, BORDERS, SHADOWS } from '../theme';
import { useAuth } from '../auth/AuthContext';
import { listMaps, renameMap, deleteMap } from '../utils/mapsRepository';
import { reopenUrl } from '../utils/mapKinds';

const formatDate = (ts) => {
    try {
        const d = ts && ts.toDate ? ts.toDate() : null;
        return d ? d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '';
    } catch (_) {
        return '';
    }
};

const Shell = ({ children }) => (
    <Box
        sx={{
            minHeight: '100vh',
            '@supports (min-height: 100dvh)': { minHeight: '100dvh' },
            backgroundColor: PALETTE.page,
            display: 'flex',
            flexDirection: 'column',
        }}
    >
        <TopNav />
        <Box component="main" sx={{ flex: 1, px: { xs: 2, sm: 3 }, py: { xs: 3, sm: 4 }, width: '100%', maxWidth: 1100, mx: 'auto' }}>
            {children}
        </Box>
        <Footer />
    </Box>
);

const MapCard = ({ map, onRename, onDelete, onReopen }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    return (
        <GlassCard variant="frame" sx={{ p: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <Box
                component={RouterLink}
                to={reopenUrl(map.type, map.id)}
                sx={{
                    display: 'block',
                    aspectRatio: '4 / 3',
                    backgroundColor: PALETTE.surfaceSubtle,
                    backgroundImage: map.thumbUrl ? `url(${map.thumbUrl})` : 'none',
                    // `contain` (not `cover`) so non-4:3 thumbnails — e.g. a
                    // portrait country silhouette from the Photo Shaper — show in
                    // full instead of being cropped to fill the card.
                    backgroundSize: 'contain',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center',
                    position: 'relative',
                }}
            >
                {!map.thumbUrl && (
                    <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <MapOutlined sx={{ fontSize: 48, color: PALETTE.textFaint }} />
                    </Box>
                )}
            </Box>

            <Box sx={{ p: 1.75, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700, color: PALETTE.text, lineHeight: 1.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {map.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: PALETTE.textMuted, display: 'block', mt: 0.25 }}>
                        {(map.breadcrumb || []).join(' › ') || 'Map'}
                    </Typography>
                    {map.updatedAt && (
                        <Typography variant="caption" sx={{ color: PALETTE.textFaint }}>
                            {formatDate(map.updatedAt)}
                        </Typography>
                    )}
                </Box>
                <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)} aria-label="Map options">
                    <MoreVert fontSize="small" />
                </IconButton>
                <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                    <MenuItem component={RouterLink} to={reopenUrl(map.type, map.id)}>Reopen</MenuItem>
                    <MenuItem onClick={() => { setAnchorEl(null); onRename(map); }}>Rename</MenuItem>
                    <MenuItem onClick={() => { setAnchorEl(null); onDelete(map); }} sx={{ color: PALETTE.danger }}>
                        Delete
                    </MenuItem>
                </Menu>
            </Box>

            <Box sx={{ px: 1.75, pb: 1.75, mt: 'auto' }}>
                <ActionButton variant="secondary" onClick={onReopen} sx={{ width: '100%', height: 40 }}>
                    Reopen
                </ActionButton>
            </Box>
        </GlassCard>
    );
};

const MyMapsPage = () => {
    useDocumentTitle('My Maps | Map Shaper');
    const navigate = useNavigate();
    const { authReady, isSignedIn, openAuthDialog } = useAuth();
    const [maps, setMaps] = useState(null); // null = not loaded yet
    const [error, setError] = useState(null);
    const [renaming, setRenaming] = useState(null); // { id, name }
    const [renameValue, setRenameValue] = useState('');
    const [deleting, setDeleting] = useState(null); // map
    const [busy, setBusy] = useState(false);

    const reload = useCallback(async () => {
        setError(null);
        try {
            const rows = await listMaps();
            setMaps(rows);
        } catch (err) {
            setError(err.message || 'Could not load your maps.');
            setMaps([]);
        }
    }, []);

    useEffect(() => {
        if (isSignedIn) reload();
        else if (authReady) setMaps([]);
    }, [isSignedIn, authReady, reload]);

    const doRename = async () => {
        const name = renameValue.trim();
        if (!name) return;
        setBusy(true);
        try {
            await renameMap(renaming.id, name);
            setRenaming(null);
            await reload();
        } catch (err) {
            setError(err.message || 'Rename failed.');
        } finally {
            setBusy(false);
        }
    };

    const doDelete = async () => {
        setBusy(true);
        try {
            await deleteMap(deleting.id);
            setDeleting(null);
            await reload();
        } catch (err) {
            setError(err.message || 'Delete failed.');
        } finally {
            setBusy(false);
        }
    };

    // Signed-out state.
    if (authReady && !isSignedIn) {
        return (
            <Shell>
                <GlassCard variant="frame" sx={{ maxWidth: 460, mx: 'auto', mt: { xs: 4, sm: 8 }, textAlign: 'center', p: 4 }}>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: PALETTE.text, mb: 1 }}>
                        Your saved maps
                    </Typography>
                    <Typography variant="body1" sx={{ color: PALETTE.textMuted, mb: 3 }}>
                        Log in to see the maps you’ve saved and reopen them anytime.
                    </Typography>
                    <ActionButton variant="primary" onClick={openAuthDialog} sx={{ width: '100%' }}>
                        Log in
                    </ActionButton>
                </GlassCard>
            </Shell>
        );
    }

    return (
        <Shell>
            <Typography variant="h4" sx={{ fontWeight: 800, color: PALETTE.text, mb: 0.5 }}>
                My Maps
            </Typography>
            <Typography variant="body2" sx={{ color: PALETTE.textMuted, mb: 3 }}>
                Reopen any saved map to download or order it again.
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {maps === null ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress sx={{ color: PALETTE.primary }} />
                </Box>
            ) : maps.length === 0 ? (
                <GlassCard variant="frame" sx={{ textAlign: 'center', p: { xs: 4, sm: 6 }, mt: 2 }}>
                    <MapOutlined sx={{ fontSize: 56, color: PALETTE.textFaint, mb: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, color: PALETTE.text, mb: 0.5 }}>
                        No saved maps yet
                    </Typography>
                    <Typography variant="body2" sx={{ color: PALETTE.textMuted, mb: 3 }}>
                        Create a map in any tool, then tap Save to keep it here.
                    </Typography>
                    <ActionButton variant="primary" onClick={() => navigate('/travel-maps')}>
                        Start a map
                    </ActionButton>
                </GlassCard>
            ) : (
                <Box
                    sx={{
                        display: 'grid',
                        gap: { xs: 1.5, sm: 2 },
                        gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' },
                    }}
                >
                    {maps.map((m) => (
                        <MapCard
                            key={m.id}
                            map={m}
                            onReopen={() => navigate(reopenUrl(m.type, m.id))}
                            onRename={(map) => { setRenaming(map); setRenameValue(map.name); }}
                            onDelete={setDeleting}
                        />
                    ))}
                </Box>
            )}

            {/* Rename dialog */}
            <Modal open={Boolean(renaming)} onClose={busy ? undefined : () => setRenaming(null)}>
                <Box sx={renameModalSx}>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: PALETTE.text, mb: 2 }}>
                        Rename map
                    </Typography>
                    <TextField
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        fullWidth
                        size="small"
                        autoFocus
                        disabled={busy}
                        inputProps={{ maxLength: 60 }}
                        onKeyDown={(e) => { if (e.key === 'Enter') doRename(); }}
                    />
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
                        <ActionButton variant="ghost" onClick={() => setRenaming(null)} disabled={busy} sx={{ minWidth: 0 }}>
                            Cancel
                        </ActionButton>
                        <ActionButton variant="primary" onClick={doRename} loading={busy} disabled={!renameValue.trim()} sx={{ minWidth: 120 }}>
                            Save
                        </ActionButton>
                    </Box>
                </Box>
            </Modal>

            {/* Delete confirm */}
            <Modal open={Boolean(deleting)} onClose={busy ? undefined : () => setDeleting(null)}>
                <Box sx={renameModalSx}>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: PALETTE.text, mb: 1 }}>
                        Delete “{deleting?.name}”?
                    </Typography>
                    <Typography variant="body2" sx={{ color: PALETTE.textMuted, mb: 2 }}>
                        This permanently removes the saved map. This can’t be undone.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <ActionButton variant="ghost" onClick={() => setDeleting(null)} disabled={busy} sx={{ minWidth: 0 }}>
                            Cancel
                        </ActionButton>
                        <ActionButton variant="primary" onClick={doDelete} loading={busy} sx={{ minWidth: 120, background: PALETTE.danger }}>
                            Delete
                        </ActionButton>
                    </Box>
                </Box>
            </Modal>
        </Shell>
    );
};

const renameModalSx = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    maxWidth: '92vw',
    backgroundColor: PALETTE.surface,
    border: BORDERS.soft,
    borderRadius: 3,
    boxShadow: SHADOWS.card,
    p: { xs: 3, sm: 4 },
};

export default MyMapsPage;
